import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Mail, Loader2, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";

const KEY_LABELS = { password_reset: "Password Reset", access_confirmation: "Access Confirmation / Welcome" };
const FIELD_LABELS = { subject: "Subject line", intro: "Body message", headline: "Headline", intro_free: "Intro (free access grant)", intro_paid: "Intro (paid purchase)" };

export default function EmailTemplateManager() {
  const [templates, setTemplates] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/email-templates");
      setTemplates(data);
      const d = {}; data.forEach((t) => { d[t.key] = { ...t.values }; });
      setDrafts(d);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (key) => {
    setSavingKey(key);
    try { await api.put(`/admin/email-templates/${key}`, drafts[key]); toast.success("Template saved"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingKey(null); }
  };
  const resetDefaults = (t) => setDrafts((d) => ({ ...d, [t.key]: { ...t.defaults } }));
  const setField = (key, field, val) => setDrafts((d) => ({ ...d, [key]: { ...d[key], [field]: val } }));

  if (!templates) return <div className="text-slate-400 text-sm p-4"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>;

  return (
    <div data-testid="admin-email-templates">
      <h3 className="font-heading font-semibold text-[#0A192F] mb-1 flex items-center gap-2"><Mail className="h-5 w-5 text-amber-600" /> Email Templates</h3>
      <p className="text-xs text-slate-500 mb-5">Edit the wording of automated emails. The branded layout and secure links are handled for you — just the text below changes.</p>
      <div className="space-y-5">
        {templates.map((t) => (
          <div key={t.key} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`admin-email-template-${t.key}`}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-heading font-semibold text-[#0A192F]">{KEY_LABELS[t.key] || t.key}</h4>
              <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => resetDefaults(t)} data-testid={`admin-email-reset-${t.key}`}><RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset to default</Button>
            </div>
            <div className="space-y-3">
              {Object.keys(t.defaults).map((field) => (
                <div key={field}>
                  <Label className="text-xs text-slate-500">{FIELD_LABELS[field] || field}</Label>
                  {field === "subject" ? (
                    <Input value={drafts[t.key]?.[field] ?? ""} onChange={(e) => setField(t.key, field, e.target.value)} className="mt-1" data-testid={`admin-email-${t.key}-${field}`} />
                  ) : (
                    <Textarea rows={2} value={drafts[t.key]?.[field] ?? ""} onChange={(e) => setField(t.key, field, e.target.value)} className="mt-1" data-testid={`admin-email-${t.key}-${field}`} />
                  )}
                </div>
              ))}
            </div>
            <Button size="sm" className="mt-4 bg-amber-600 hover:bg-amber-700 text-white" disabled={savingKey === t.key} onClick={() => save(t.key)} data-testid={`admin-email-save-${t.key}`}>
              {savingKey === t.key ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />} Save
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
